import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { CalendarEventModel } from '../../../../../../types';
import { deleteCalendarEvent, UserKeys } from '../../../../../api/users-api';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import SharedDialog from '../../../../../shared/shared-dialog';
import { handleErrors } from '../../../../../utils/errors';
import { useStore } from '../../../../state/zustand-store';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
interface CalendarEventsDeleteDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedEvent: CalendarEventModel;
  changeLogRoles: string[];
  calendarId: string;
  isAllUsers: boolean;
  dateRange: {
    start: number;
    end: number;
  };
}

export default function CalendarEventsDeleteDialog({
  open,
  handleClose,
  userEmail,
  calendarId,
  selectedEvent,
  changeLogRoles,
  isAllUsers,
  dateRange,
}: CalendarEventsDeleteDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [deleteAll, setDeleteAll] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteCalendarEvent(
        userEmail,
        calendarId,
        {
          id: deleteAll ? selectedEvent.recurringEventId! : selectedEvent.id,
          summary: selectedEvent.summary,
        },
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: deleteAll
          ? 'Calendar event series deleted successfully!'
          : 'Calendar event deleted successfully!',
      });

      if (deleteAll) {
        queryClient.setQueriesData(
          {
            queryKey: [
              UserKeys.CalendarEvents,
              calendarId,
              userEmail,
              {
                start: dateRange.start,
                end: dateRange.end,
              },
            ],
          },
          (oldData: CalendarEventModel[] | undefined) => {
            if (oldData) {
              return oldData.filter(
                (event) =>
                  event.recurringEventId !== selectedEvent.recurringEventId
              );
            } else {
              return [];
            }
          }
        );
      } else {
        queryClient.setQueriesData(
          {
            queryKey: [
              UserKeys.CalendarEvents,
              calendarId,
              userEmail,
              {
                start: dateRange.start,
                end: dateRange.end,
              },
            ],
          },
          (oldData: CalendarEventModel[] | undefined) => {
            if (oldData) {
              return oldData.filter((event) => event.id !== selectedEvent.id);
            } else {
              return [];
            }
          }
        );
      }

      setChangeLogMessage('');
      setDeleteAll(false);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting this calendar event. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        isAllUsers
          ? analytics.calendars.calendars.delete_calendar_event
          : analytics.user_management.user_details.calendars
              .delete_calendar_event,
        isAllUsers
          ? {
              module: ModuleList.modules.calendars.name,
              section: ModuleList.modules.calendars.sections.calendars,
            }
          : {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.sections.calendars,
            }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, CalendarRoles.RemoveEvents.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      setDeleteAll(false);
      mutate();
    }
  }

  function handleDeleteAll() {
    if (
      isAbleToSeeUIElement(changeLogRoles, CalendarRoles.RemoveAllEvents.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      setDeleteAll(true);
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Delete Calendar Event"
      dialogContent={
        <Box>
          <Typography
            sx={{
              mt: 1,
            }}
          >
            Do you wish to delete calendar event:{' '}
            <strong>{selectedEvent?.summary}</strong> from{' '}
            <strong>{userEmail}</strong>'s calendar?
            {selectedEvent.recurringEventId
              ? ' You can also delete all events in this series by clicking "DELETE ALL EVENTS".'
              : ''}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete Event',
        action: handleSubmit,
        loading: isPending && !deleteAll,
        disabled: isPending,
        color: 'error',
      }}
      secondaryButton={
        selectedEvent.recurringEventId
          ? {
              title: 'Delete All Events',
              action: handleDeleteAll,
              loading: isPending && deleteAll,
              disabled: isPending,
            }
          : undefined
      }
    />
  );
}
